import { useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { 
  Layers, 
  AlertTriangle, 
  Eye, 
  Type, 
  Image, 
  FileText, 
  Package, 
  DollarSign,
  Pen,
  FileSignature,
  Video,
  MousePointer,
  Shapes,
  Sparkles
} from "lucide-react";
import type { Block, Page } from "@/types/proposals";
import { cn } from "@/lib/utils";

interface BlocksOutlinePanelProps {
  currentPage?: Page;
  selectedBlockIds: string[];
  onSelectBlock: (blockId: string) => void;
  onBringToView: (blockId: string) => void;
  canvasWidth: number;
  canvasHeight: number;
}

const BLOCK_ICONS: Record<string, any> = {
  text: Type,
  image: Image,
  cover: FileText,
  line_items_table: Package,
  totals_summary: DollarSign,
  signature: Pen,
  terms: FileSignature,
  video_embed: Video,
  button: MousePointer,
  shape: Shapes,
  smart_field: Sparkles,
  good_better_best: Package,
  marketing_gallery: Image,
  dynamic_image: Image,
  package_selector: Package,
  icon: Sparkles,
};

export function BlocksOutlinePanel({
  currentPage,
  selectedBlockIds,
  onSelectBlock,
  onBringToView,
  canvasWidth,
  canvasHeight,
}: BlocksOutlinePanelProps) {
  const [isExpanded, setIsExpanded] = useState(false);

  const checkIfOffCanvas = (block: Block): boolean => {
    const { x, y, width, height } = block.position;
    const effectiveHeight = height === 'auto' ? 100 : height;
    
    return (
      x < 0 || 
      y < 0 || 
      x + width > canvasWidth || 
      y + effectiveHeight > canvasHeight
    );
  };

  const getBlockName = (block: Block): string => {
    if (block.type === 'text' && block.content?.text) {
      const text = block.content.text.replace(/<[^>]*>/g, '').trim();
      return text.substring(0, 30) + (text.length > 30 ? '...' : '');
    }
    return block.type.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
  };

  if (!currentPage) return null;

  const offCanvasBlocks = currentPage.blocks.filter(checkIfOffCanvas);

  return (
    <Card className={cn(
      "absolute top-4 right-4 z-50 bg-background/95 backdrop-blur-sm border-border shadow-lg transition-all",
      isExpanded ? "w-80" : "w-12"
    )}>
      <div className="flex items-center justify-between p-3 border-b">
        {isExpanded ? (
          <>
            <div className="flex items-center gap-2">
              <Layers className="w-4 h-4 text-muted-foreground" />
              <h3 className="font-semibold text-sm">Layers</h3>
              {offCanvasBlocks.length > 0 && (
                <Badge variant="destructive" className="ml-auto">
                  {offCanvasBlocks.length}
                </Badge>
              )}
            </div>
            <Button 
              variant="ghost" 
              size="sm"
              onClick={() => setIsExpanded(false)}
              className="h-7 w-7 p-0"
            >
              <Eye className="w-4 h-4" />
            </Button>
          </>
        ) : (
          <Button 
            variant="ghost" 
            size="sm"
            onClick={() => setIsExpanded(true)}
            className="h-7 w-7 p-0 mx-auto"
          >
            <Layers className="w-4 h-4" />
          </Button>
        )}
      </div>

      {isExpanded && (
        <ScrollArea className="h-[500px]" type="always">
          <div className="p-3 space-y-2">
            {currentPage.blocks.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground text-sm">
                No blocks on this page
              </div>
            ) : (
              currentPage.blocks
                .sort((a, b) => b.position.zIndex - a.position.zIndex)
                .map((block) => {
                  const Icon = BLOCK_ICONS[block.type] || Type;
                  const isOffCanvas = checkIfOffCanvas(block);
                  const isSelected = selectedBlockIds.includes(block.id);

                  return (
                    <div
                      key={block.id}
                      className={cn(
                        "flex items-center gap-2 p-2 rounded-md cursor-pointer transition-colors group",
                        isSelected 
                          ? "bg-primary text-primary-foreground" 
                          : "hover:bg-muted",
                        isOffCanvas && "border border-destructive"
                      )}
                      onClick={() => onSelectBlock(block.id)}
                    >
                      <Icon className="w-4 h-4 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium truncate">
                          {getBlockName(block)}
                        </div>
                        <div className="text-xs opacity-70">
                          {Math.round(block.position.x)}, {Math.round(block.position.y)} · 
                          {Math.round(block.position.width)}×{block.position.height === 'auto' ? 'auto' : Math.round(block.position.height)}
                        </div>
                      </div>
                      {isOffCanvas && (
                        <div className="flex items-center gap-1">
                          <AlertTriangle className="w-4 h-4 text-destructive flex-shrink-0" />
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 px-2 opacity-0 group-hover:opacity-100 transition-opacity"
                            onClick={(e) => {
                              e.stopPropagation();
                              onBringToView(block.id);
                            }}
                          >
                            <Eye className="w-3 h-3 mr-1" />
                            Fix
                          </Button>
                        </div>
                      )}
                    </div>
                  );
                })
            )}
          </div>
        </ScrollArea>
      )}
    </Card>
  );
}